import { useState } from 'react';
import { Lightbulb, Loader, Check } from 'lucide-react';
import { getStructuredResponse } from '../../services/openaiService';
import { trackEvent } from '../../hooks/useGoogleAnalytics';

const SmartFormAssistant = ({ onSuggestionApply, currentFormData = {} }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const analyzeFormData = async () => {
    if (!currentFormData || Object.keys(currentFormData)?.length === 0) {
      return;
    }

    setIsAnalyzing(true);
    trackEvent('ai_form_analysis', 'engagement', 'analysis_requested');

    try {
      const formDescription = Object.entries(currentFormData)?.filter(([key, value]) => value && value?.toString()?.trim())?.map(([key, value]) => `${key}: ${value}`)?.join(', ');

      const analysisPrompt = `
        Based on this customer inquiry: "${formDescription}"
        
        Please provide intelligent suggestions for a cleaning service booking form. Consider:
        1. What specific cleaning services would be most appropriate
        2. Estimated time and cost range
        3. Any special considerations or add-on services
        4. Recommended frequency (one-time, weekly, monthly, etc.)
        5. Important questions to ask the customer
      `;

      const customSchema = {
        type: 'object',
        properties: {
          recommended_services: {
            type: 'array',
            items: { type: 'string' },
            description: 'List of recommended cleaning services'
          },
          estimated_duration: { 
            type: 'string', 
            description: 'Estimated cleaning duration' 
          },
          estimated_cost: { 
            type: 'string', 
            description: 'Estimated cost range' 
          },
          frequency_suggestion: { 
            type: 'string', 
            description: 'Recommended cleaning frequency' 
          },
          special_considerations: {
            type: 'array',
            items: { type: 'string' },
            description: 'Special considerations or requirements'
          },
          additional_questions: {
            type: 'array',
            items: { type: 'string' },
            description: 'Important questions to ask the customer'
          },
          priority_level: {
            type: 'string',
            enum: ['standard', 'urgent', 'flexible'],
            description: 'Suggested priority level'
          }
        },
        required: ['recommended_services', 'estimated_duration', 'estimated_cost'],
        additionalProperties: false,
      };

      const response = await getStructuredResponse(analysisPrompt, customSchema);
      setSuggestions(response);
      setShowSuggestions(true);
      trackEvent('ai_form_analysis', 'success', 'suggestions_generated');

    } catch (error) {
      console.error('Error analyzing form data:', error);
      trackEvent('ai_form_analysis', 'error', error?.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const applySuggestion = (suggestionType, value) => {
    if (onSuggestionApply) {
      onSuggestionApply(suggestionType, value);
    }
    trackEvent('ai_form_suggestion', 'engagement', suggestionType);
  };

  const hasFormData = currentFormData && Object.values(currentFormData)?.some(value => 
    value && value?.toString()?.trim()
  );

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-blue-800">AI Smart Assistant</h3>
      </div>
      {!hasFormData ? (
        <p className="text-blue-700 text-sm">
          Start filling out the form, and I'll provide intelligent suggestions based on your needs!
        </p>
      ) : (
        <>
          <p className="text-blue-700 text-sm mb-3">
            I can analyze your requirements and provide personalized recommendations.
          </p>

          <button
            onClick={analyzeFormData}
            disabled={isAnalyzing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {isAnalyzing ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Lightbulb className="w-4 h-4" />
                Get AI Recommendations
              </>
            )}
          </button>
        </>
      )}
      {/* Suggestions Display */}
      {showSuggestions && suggestions && (
        <div className="mt-4 space-y-3">
          <h4 className="font-medium text-blue-800 border-b border-blue-200 pb-1">
            AI Recommendations
          </h4>

          {/* Recommended Services */}
          {suggestions?.recommended_services && suggestions?.recommended_services?.length > 0 && (
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <h5 className="font-medium text-gray-800 mb-2">Recommended Services</h5>
              <div className="flex flex-wrap gap-2">
                {suggestions?.recommended_services?.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => applySuggestion('service', service)}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs hover:bg-blue-200 transition-colors flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    {service}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Time and Cost Estimates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {suggestions?.estimated_duration && (
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <h5 className="font-medium text-gray-800 text-sm">Estimated Duration</h5>
                <p className="text-blue-600 font-semibold">{suggestions?.estimated_duration}</p>
              </div>
            )}

            {suggestions?.estimated_cost && (
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <h5 className="font-medium text-gray-800 text-sm">Estimated Cost</h5>
                <p className="text-green-600 font-semibold">{suggestions?.estimated_cost}</p>
              </div>
            )}
          </div>

          {/* Frequency Suggestion */}
          {suggestions?.frequency_suggestion && (
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <h5 className="font-medium text-gray-800 mb-2">Recommended Frequency</h5>
              <button
                onClick={() => applySuggestion('frequency', suggestions?.frequency_suggestion)}
                className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm hover:bg-green-200 transition-colors flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                {suggestions?.frequency_suggestion}
              </button>
            </div>
          )}

          {/* Special Considerations */}
          {suggestions?.special_considerations && suggestions?.special_considerations?.length > 0 && (
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <h5 className="font-medium text-gray-800 mb-2">Special Considerations</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                {suggestions?.special_considerations?.map((consideration, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                    {consideration}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Questions */}
          {suggestions?.additional_questions && suggestions?.additional_questions?.length > 0 && (
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <h5 className="font-medium text-gray-800 mb-2">Consider Asking</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                {suggestions?.additional_questions?.map((question, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-400">?</span>
                    {question}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartFormAssistant;